import { Box, Button, Theme, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { getIntentButtonColors } from '../../utils/intentButtonStyle';
import { LANGUAGE_SWITCH_INTENT_CARD } from '../../utils/constants';
import LanguageSwitch from '../LangaugeSwitch';
import { IntentCard, Intents } from '../../types/intent';
import useGroupedIntents from './useGroupedIntents.hooks';
import useIntentHotkeys from './useIntentHotkeys.hooks';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PanToolOutlinedIcon from '@mui/icons-material/PanToolOutlined';

// HITL 3.0 bottom-bar grouping of intents. Each intent is placed in a section
// by its `intent` enum value; anything unmapped falls into a trailing "More"
// group so no card is ever silently dropped. Button background + text colors
// come from the shared getIntentButtonColors helper with isHitl3 = true, which
// applies the solid section scheme (amber / blue / red); the legacy layout keeps
// each card's own colors. The click/dispatch behavior comes from this layout's
// own `useGroupedIntents` hook — independent of the OLD IntentsSection so the
// two can't affect each other.
interface IntentSection {
  label: string;
  intents: Intents[];
  Icon: React.ElementType;
}

const INTENT_SECTIONS: IntentSection[] = [
  {
    label: 'Conversation',
    intents: [
      Intents.greet,
      Intents.acknowledge,
      Intents.askRepeat,
      Intents.askMoment,
    ],
    Icon: ChatBubbleOutlineOutlinedIcon,
  },
  {
    label: 'Order Flow',
    intents: [
      Intents.continue,
      Intents.confirm,
      Intents.readback,
      Intents.complete,
    ],
    Icon: AccountTreeOutlinedIcon,
  },
  {
    label: 'Escalation',
    intents: [Intents.requestStaff],
    Icon: AutoAwesomeOutlinedIcon,
  },
];

const FALLBACK_SECTION: Omit<IntentSection, 'intents'> = {
  label: 'More',
  Icon: ChatBubbleOutlineOutlinedIcon,
};

// The four primaries also have a single-key arrow fast-path (handled in the
// OmniBar). Surfacing the glyph on the pill keeps that otherwise-invisible
// mapping discoverable alongside the Alt+digit badge.
const ARROW_GLYPH: Partial<Record<Intents, string>> = {
  [Intents.acknowledge]: '↑',
  [Intents.askMoment]: '←',
  [Intents.continue]: '→',
  [Intents.readback]: '↓',
};

// Escalation (Request Staff) is pulled to the FRONT of the intent cards so it
// sits directly to the right of the Quick Response input — out of the
// bottom-right corner where it previously stacked under the Cancel / Finish
// Order CTAs (red-on-red, misclick-prone). The remaining, lower-consequence
// groups follow. Derived from the requestStaff intent so a label rename can't
// silently break the ordering.
const ESCALATION_SECTION =
  INTENT_SECTIONS.find((section) =>
    section.intents.includes(Intents.requestStaff)
  )?.label ?? 'Escalation';

const TRAILING_SECTIONS = [
  ...INTENT_SECTIONS.map((section) => section.label).filter(
    (label) => label !== ESCALATION_SECTION
  ),
  FALLBACK_SECTION.label,
];

// Order Flow (Continue / Confirm / Read Back / Complete). Derived from the
// `continue` intent so a label rename can't silently move the OmniBar, which is
// rendered immediately before this section (see render below).
const ORDER_FLOW_SECTION =
  INTENT_SECTIONS.find((section) => section.intents.includes(Intents.continue))
    ?.label ?? 'Order Flow';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    // One flush row (no per-group cards): Escalation · Language · Conversation ·
    // Quick Response · Order Flow · More. The embedded input takes the leftover
    // width; the groups hug their buttons.
    flex: '1 1 auto',
    minWidth: 0,
    gap: theme.spacing(1),
    alignItems: 'stretch',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    // Flush cluster — no card frame. Hugs the buttons it holds (no min-width /
    // flex-grow) so a single-button group like Escalation leaves no trailing
    // space; still shrinkable as a graceful fallback on narrow windows.
  },
  groupButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignContent: 'flex-start',
    alignItems: 'center',
    flex: 1,
  },
  // Language-switch cluster, sibling to the intent groups. Only rendered for
  // bilingual restaurants. Flush (no card frame) — the toggle itself carries the
  // border now. Sized to its content (not flex-grown).
  languageGroup: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flexShrink: 0,
    minWidth: 150,
  },
  languageSwitch: {
    display: 'flex',
    // White track with a hairline border so the English / Spanish toggle reads
    // as a segmented control on the flush white bar. A bit larger than the
    // intent pills so it's easier to hit.
    backgroundColor: Colors.white,
    border: `1px solid ${Colors.alto}`,
    borderRadius: theme.spacing(2.5),
    padding: '3px',
    height: 40,
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    width: '100%',
  },
  languageOption: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    borderRadius: theme.spacing(2.25),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 'bold',
    // Title-case the 'english' / 'spanish' values → "English" / "Spanish".
    textTransform: 'capitalize',
    zIndex: 1,
    '&.disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
  languageOptionActive: {
    backgroundColor: 'rgb(0, 107, 166)',
    color: 'white',
  },
  languageOptionInactive: {
    backgroundColor: 'transparent',
    color: '#4f5153',
  },
}));

interface IntentsSectionGroupedProps {
  /**
   * Quick Response input (OmniBar) node. Rendered inside the bar immediately
   * before the Order Flow group so it sits between the Conversation primaries
   * and Continue / Confirm / Read Back / Complete. Omit to render the intent
   * groups only.
   */
  omniBar?: React.ReactNode;
}

export default function IntentsSectionGrouped({
  omniBar,
}: IntentsSectionGroupedProps = {}) {
  const classes = useStyles();
  const {
    intents,
    intentsStatus,
    isBilingual,
    isSpanish,
    isStaffInterventionHappening,
    onIntentDown,
    onIntentUp,
    fireIntentByShortcut,
    shortcutNumberByIdx,
    languageSwitch,
  } = useGroupedIntents();

  // Alt+1…N mirror the on-pill number badges (contiguous over reachable intents).
  useIntentHotkeys(fireIntentByShortcut);

  // Keep each intent's original (filtered) index so click handling and
  // intentsStatus lookups stay identical to the flat layout.
  const intentsWithIdx = intents.map((intent, idx) => ({ intent, idx }));

  const sectionFor = (intent: Intents) =>
    INTENT_SECTIONS.find((section) => section.intents.includes(intent))
      ?.label ?? FALLBACK_SECTION.label;

  const groupedIntents = intentsWithIdx.reduce<
    Record<string, { intent: IntentCard; idx: number }[]>
  >((groups, entry) => {
    const label = sectionFor(entry.intent.intent);
    (groups[label] = groups[label] ?? []).push(entry);
    return groups;
  }, {});

  const renderGroup = (label: string) => {
    const entries = groupedIntents[label];
    if (!entries?.length) return null;
    // Escalation (Request Staff) is the one high-consequence control in the bar,
    // so it keeps extra emphasis — uppercase label, raised-hand icon and heavier
    // weight. Its colors (solid red) come from the shared intent-button helper,
    // matching the legacy layout.
    const isEscalation = label === ESCALATION_SECTION;
    return (
      <div key={label} className={classes.group}>
        <div className={classes.groupButtons}>
          {entries.map(({ intent: intentCard, idx }) => {
            const clickable = !!intentsStatus[idx]?.clickable;
            // Background + text come from the shared getIntentButtonColors helper
            // with isHitl3 = true → the solid section scheme (amber Conversation,
            // blue Order Flow, red Escalation). Only the structural affordances
            // (size, grouping, Alt+digit keycap, arrow/raised-hand glyphs) are
            // specific to this layout.
            const { backgroundColor, color } = getIntentButtonColors(
              intentCard,
              clickable,
              true
            );
            // One solid style for every pill. Escalation (Request Staff) only
            // adds emphasis — uppercase, heavier weight and the raised-hand icon
            // — not a different color treatment: it is solid red like legacy.
            const buttonSx = {
              height: 34,
              flex: '0 0 auto',
              minWidth: 64,
              px: isEscalation ? 1.25 : 0.75,
              borderRadius: '4px',
              backgroundColor,
              color,
              textTransform: isEscalation ? 'uppercase' : 'none',
              letterSpacing: isEscalation ? '0.04em' : 'normal',
              fontWeight: isEscalation ? 700 : 500,
              fontSize: HITL3_FONT_SIZE,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              // Staff intervention fades every pill to signal it's inert (the
              // click/TTS path is already blocked in useGroupedIntents). The
              // Mui-disabled override below preserves the group color, so only
              // this opacity communicates the disabled state.
              opacity: isStaffInterventionHappening ? 0.5 : 1,
              '& .MuiButton-startIcon': { marginRight: '4px' },
              '&:hover': { backgroundColor },
              // Keep the group's text color on the shared disabled grey (parity
              // with legacy's inline style) instead of MUI's greyed-out default.
              '&.Mui-disabled': { backgroundColor, color },
            };
            // Alt+number badge: the intent's contiguous shortcut number (Request
            // Staff has none → no badge). Layout is uniform across pills:
            // [number] [label] [optional arrow].
            const shortcutNumber = shortcutNumberByIdx[idx];
            const showBadge = !isEscalation && shortcutNumber !== undefined;
            const arrowGlyph = !isEscalation
              ? ARROW_GLYPH[intentCard.intent]
              : undefined;
            const keyHint = showBadge
              ? `Alt+${shortcutNumber}${arrowGlyph ? ` · ${arrowGlyph}` : ''}`
              : '';
            // The keycap is a WHITE chip, so its number needs a dark-enough ink.
            // For dark buttons (white text) the button color reads fine on white;
            // for light buttons (the amber Conversation group, which use dark
            // text) the button color (amber) would be nearly invisible, so fall
            // back to near-black — keeps the 1–4 numbers legible.
            const keycapInk =
              color === Colors.white
                ? backgroundColor
                : Colors.primaryButtonTextColor;
            const keycapSx = {
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 15,
              height: 15,
              px: '3px',
              mr: 0.5,
              borderRadius: '3px',
              border: `1px solid ${Colors.white}`,
              backgroundColor: Colors.white,
              color: keycapInk,
              fontSize: '0.62rem',
              fontWeight: 700,
              lineHeight: 1,
            };
            return (
              // Tooltip needs a non-disabled wrapper (the span) to receive hover
              // events while the pill is in its cooldown (disabled) state.
              <Tooltip
                key={`intent-${idx}`}
                title={keyHint}
                arrow
                disableInteractive
              >
                <span>
                  <Button
                    onMouseDown={() => onIntentDown()}
                    onMouseUp={() => onIntentUp(intentCard, idx)}
                    disableElevation
                    disabled={!clickable || isStaffInterventionHappening}
                    startIcon={
                      isEscalation ? (
                        <PanToolOutlinedIcon sx={{ fontSize: 15 }} />
                      ) : undefined
                    }
                    // MUI Button's own emotion styles override makeStyles
                    // classes, so visual styling lives in `sx`.
                    sx={buttonSx}
                  >
                    {showBadge && (
                      <Box component="span" sx={keycapSx}>
                        {shortcutNumber}
                      </Box>
                    )}
                    {intentCard.title}
                    {arrowGlyph && (
                      <Box
                        component="span"
                        sx={{ fontSize: '0.66rem', ml: 0.5, opacity: 0.85 }}
                      >
                        {arrowGlyph}
                      </Box>
                    )}
                  </Button>
                </span>
              </Tooltip>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div id="intentsSection" className={classes.container}>
      {/* Escalation stays leftmost — out of the red-on-red bottom-right corner. */}
      {renderGroup(ESCALATION_SECTION)}
      {isBilingual && (
        <div className={classes.languageGroup}>
          <LanguageSwitch
            classes={classes}
            isSpanish={isSpanish}
            onIntentUp={languageSwitch}
            intentCard={LANGUAGE_SWITCH_INTENT_CARD}
          />
        </div>
      )}
      {TRAILING_SECTIONS.map((label) => (
        <React.Fragment key={label}>
          {/* Quick Response input immediately precedes the Order Flow primaries
              (Continue / Confirm / Read Back / Complete). */}
          {label === ORDER_FLOW_SECTION && omniBar}
          {renderGroup(label)}
        </React.Fragment>
      ))}
    </div>
  );
}
